import { formatDate } from "@/lib/format";
// web/src/pages/external/ExternalInvoicePage.tsx
//
// I-16 — Public anonymous invoice view (token-bearing).
// Mirrors ExternalStatementViewPage: no AppShell, no auth wrapper, just a
// centered card with the Allmark logo, summary and a "Download PDF" anchor.
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  getExternalInvoice,
  getExternalInvoicePdfUrl,
} from "@/lib/invoices-payments";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function ExternalInvoicePage() {
  const { token = "" } = useParams();
  const { data: view, isLoading, error } = useQuery({
    queryKey: ["external-invoice", token],
    queryFn: () => getExternalInvoice(token),
    retry: false,
  });

  if (isLoading) return <p className="p-8 text-center">Loading…</p>;
  if (error)
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-semibold mb-2">Link is no longer valid</h1>
        <p className="text-muted-foreground">{(error as Error).message}</p>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between border-b pb-3">
        <img src="/allmark-logo.png" alt="Allmark" className="h-10 w-auto" />
        <h1 className="text-xl font-semibold">Invoice {view!.invoiceReferenceNo}</h1>
      </header>
      <div className="rounded-md border p-4 space-y-2">
        <p>
          <span className="text-muted-foreground">Issued: </span>
          {view!.invoiceDate
            ? formatDate(view!.invoiceDate)
            : "—"}
        </p>
        <p>
          <span className="text-muted-foreground">Due: </span>
          {view!.dueDate
            ? formatDate(view!.dueDate)
            : "—"}
        </p>
        <p>
          <span className="text-muted-foreground">Total: </span>
          <strong>{fmt.format(view!.invoiceTotalIncGst)}</strong>
        </p>
        <p>
          <span className="text-muted-foreground">Balance outstanding: </span>
          <strong>{fmt.format(view!.balanceOutstanding)}</strong>
        </p>
      </div>
      <a
        href={getExternalInvoicePdfUrl(token)}
        className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Download PDF
      </a>
    </div>
  );
}
